import UseCasePage, {
  type UseCaseFaq,
  type UseCaseScene,
  type UseCaseStat,
  type UseCaseWhy,
} from "@/components/public/use-case-page"
import type { SeoMeta } from "@/types"

const WHY: UseCaseWhy[] = [
  {
    title: "Portals alert buyers, but only once",
    description: "A Rightmove price reduction triggers a single email. A social video ad can keep that reduction in feed for a week across Instagram, TikTok and Facebook \u2014 to buyers who never set up the alert.",
  },
  {
    title: "Shows vendors you're actively marketing",
    description: "Most vendors ask \u201Cwhat are you doing differently?\u201D after a reduction. Sending them a branded social video ad that's already live is the easiest answer to give. It reframes the conversation from passive to proactive.",
  },
  {
    title: "Price drops trigger urgency bias",
    description: "Buyers who've already seen the listing at the old price see the reduction as a deal, not a warning sign. A video ad frames the reduction positively \u2014 \u201Cprice adjusted, new opportunity\u201D rather than \u201Cstruggling to sell.\u201D",
  },
  {
    title: "QuiteRight detects the price change automatically",
    description: "When you update the listing in QuiteRight (or it's re-scraped from the portal), the pipeline flags it as a price-reduction candidate and suggests a fresh render with the new figure baked in.",
  },
]

const SCENES: UseCaseScene[] = [
  { title: "\"Price reduction\" opener", duration: "0:00\u20130:03", description: "Strong visual cue \u2014 strikethrough old price, animated arrow to new price. First 3 seconds earn the scroll." },
  { title: "The refreshed hero shot", duration: "0:03\u20130:07", description: "Best exterior photo. Caption reinforces the new positioning: \u201CNew price \u00A3X,XXX\u201D with a clean, confident on-screen type." },
  { title: "Two interior highlights", duration: "0:07\u20130:15", description: "Kitchen and living area. Short feature captions that focus on lifestyle not specs \u2014 this video is re-activating interest, not introducing the property." },
  { title: "Why it's now great value", duration: "0:15\u20130:22", description: "A short on-screen line: e.g. \u201CNow below \u00A3X per sq ft\u201D or \u201C\u00A3Y under asking in area.\u201D The voiceover frames the price as the buyer's opportunity." },
  { title: "Urgency CTA", duration: "0:22\u20130:30", description: "\u201CBook a viewing this week\u201D CTA with phone number and DM prompt. The voiceover closes with a clear ask." },
]

const STATS: UseCaseStat[] = [
  { value: "2\u00D7", label: "buyer attention", detail: "A price-drop video reaches buyers who missed the portal alert, extending the reduction's half-life." },
  { value: "Auto", label: "detection", detail: "QuiteRight flags price changes in listing data and suggests a fresh render with the new figure." },
  { value: "3", label: "aspect ratios", detail: "Reels, feed, and Shorts in one render \u2014 the reduction story plays everywhere." },
  { value: "15\u201330s", label: "runtime", detail: "Short, urgent, and scroll-stopping. Long enough to show value, short enough to hold the viewer." },
]

const FAQ: UseCaseFaq[] = [
  {
    q: "Doesn't a price reduction video signal weakness?",
    a: "Only if the framing is weak. A well-made reduction video reframes the drop as an opportunity for buyers \u2014 not a concession from the vendor. The opener shows the new price confidently, the body of the video reinforces value, and the CTA creates urgency. Done right, it accelerates offers.",
  },
  {
    q: "When should I post a price reduction video?",
    a: "Within 24 hours of the portal price change going live. Portal alerts fire once; social fills the gap for the following week. Most QuiteRight customers set an auto-publish rule so the video posts the moment the listing data updates.",
  },
  {
    q: "Should I say the old price?",
    a: "Yes \u2014 visually. QuiteRight's price-reduction template shows the old price with a strikethrough and animates the arrow to the new price. Saying it in the voiceover is optional; many agents prefer to let the visual do the work and keep the spoken script focused on value.",
  },
  {
    q: "Does QuiteRight know when a listing has been reduced?",
    a: "Yes. When the listing is re-scraped or manually updated and the price changes, QuiteRight flags the listing as eligible for a price-reduction render and can auto-publish using the price-reduction template if you've turned that rule on.",
  },
  {
    q: "Can I use this for rental price reductions too?",
    a: "Absolutely. The same template works for a lettings instruction with an updated rent. Particularly effective for student lets and sticky high-end rentals where a fresh push on socials can move weeks of void cost.",
  },
]

export default function PriceReductionVideo({ meta }: { meta: SeoMeta }) {
  return (
    <UseCasePage
      meta={meta}
      useCaseName="Price Reduction Video"
      breadcrumbName="Price Reduction Video"
      heroHeadline="Reframe a price drop as an opportunity, not a concession"
      heroSubhead="A social video ad posted within 24 hours of a Rightmove price reduction keeps the story in feed for a week, reaches buyers who never set up alerts, and reassures vendors that the listing is actively marketed."
      whyHeadline="Why the price-reduction video is one of the highest-ROI ads you can run"
      whySubhead="Portal alerts fire once. Social keeps reminding the market. A well-framed reduction video turns an awkward moment into a buying signal."
      whyItems={WHY}
      anatomyHeadline="Anatomy of a price-reduction video"
      anatomySubhead="QuiteRight's price-reduction template, rendered automatically when the listing price changes."
      scenes={SCENES}
      stats={STATS}
      faq={FAQ}
      ctaHeadline="Turn every price drop into a buying signal"
      ctaBody="Auto-detect price changes, render a social video ad framed around value, and publish to every channel \u2014 without a negotiator lifting a finger."
      relatedKey="use-cases/price-reduction-video"
    />
  )
}
